// The "Condo replies" of an inbox in co-living mode (`co_living_replies` on the
// inbox chatbot config). One object with fixed keys for the built-in replies,
// a reply per emergency type, and up to ten custom rows. A built-in left empty
// uses the default wording the server returns as `co_living_reply_defaults`.

export const BUILTIN_KEYS = [
  'media_photo_no_caption',
  'media_video',
  'media_voice',
  'media_other',
  'not_whitelisted_reply',
];
export const SAFETY_KEYS = ['gas', 'fire', 'flood', 'electrical', 'break_in'];

export const REPLY_MAX = 1000;
export const MAX_CUSTOM_ROWS = 10;
const SCENARIO_MAX = 60;
const TRIGGER_MAX = 200;
const INSTRUCTION_MAX = 2000;

export function emptyCustomRow() {
  return { scenario: '', trigger: '', instruction: '', text: '', exact: true };
}

const text = value => (typeof value === 'string' ? value : '');

// The stored object has every key present, unset = null. The form works with
// strings only (empty = use the default).
export function normalizeReplies(stored) {
  const source = stored || {};
  const safety = source.safety_instruction || {};
  const form = { safety_instruction: {}, custom: [] };
  BUILTIN_KEYS.forEach(key => {
    form[key] = text(source[key]);
  });
  SAFETY_KEYS.forEach(key => {
    form.safety_instruction[key] = text(safety[key]);
  });
  form.custom = (Array.isArray(source.custom) ? source.custom : []).map(
    row => ({
      scenario: text(row.scenario),
      trigger: text(row.trigger),
      instruction: text(row.instruction),
      text: text(row.text),
      exact: row.exact !== false,
    })
  );
  return form;
}

// Returns { 'path': CODE } with the codes of CO_LIVING.REPLIES.ERRORS. Paths:
// a built-in key, `safety_instruction.gas`, `custom.0.scenario`, `custom`.
export function validateReplies(form) {
  const errors = {};
  BUILTIN_KEYS.forEach(key => {
    if (form[key].length > REPLY_MAX) errors[key] = 'TOO_LONG';
  });
  SAFETY_KEYS.forEach(key => {
    if (form.safety_instruction[key].length > REPLY_MAX) {
      errors[`safety_instruction.${key}`] = 'TOO_LONG';
    }
  });
  if (form.custom.length > MAX_CUSTOM_ROWS) errors.custom = 'TOO_MANY';
  form.custom.forEach((row, index) => {
    const path = field => `custom.${index}.${field}`;
    if (!row.scenario.trim() || row.scenario.length > SCENARIO_MAX) {
      errors[path('scenario')] = 'SCENARIO';
    }
    if (row.trigger.length > TRIGGER_MAX) errors[path('trigger')] = 'TRIGGER';
    if (!row.instruction.trim() || row.instruction.length > INSTRUCTION_MAX) {
      errors[path('instruction')] = 'INSTRUCTION';
    }
    if (!row.text.trim() || row.text.length > REPLY_MAX) {
      errors[path('text')] = 'TEXT';
    }
  });
  return errors;
}

// Back to the API shape: a blank built-in is null (use the default). The whole
// object replaces the stored one, `{}` would reset every reply.
export function repliesPayload(form) {
  const blankToNull = value => (value.trim() ? value.trim() : null);
  const payload = { safety_instruction: {} };
  BUILTIN_KEYS.forEach(key => {
    payload[key] = blankToNull(form[key]);
  });
  SAFETY_KEYS.forEach(key => {
    payload.safety_instruction[key] = blankToNull(form.safety_instruction[key]);
  });
  payload.custom = form.custom.map(row => ({
    scenario: row.scenario.trim(),
    trigger: blankToNull(row.trigger),
    instruction: row.instruction.trim(),
    text: row.text.trim(),
    exact: row.exact,
  }));
  return payload;
}

// The reply the bot will use for a built-in or an emergency type: what the
// admin wrote, else the server's default.
export function effectiveReply(form, defaults, key) {
  const [group, name] = key.split('.');
  const mine = name ? form.safety_instruction[name] : form[group];
  const fallback = name
    ? (defaults.safety_instruction || {})[name]
    : defaults[group];
  return mine.trim() ? mine : text(fallback);
}

// Why a save was refused as a list of "path: message" lines. The API's
// `invalid_replies` carries `errors: [{field, message}]`.
export function serverReplyErrors(detail) {
  return (detail && Array.isArray(detail.errors) ? detail.errors : []).map(
    item => ({
      field: String(item.field || ''),
      message: String(item.message || ''),
    })
  );
}
